import axios from 'axios';
import { useRef, useState } from 'react';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { TextInput } from '../../components/Form/TextInput';
import { Icon, close } from '../../components/Icon/Icon';
import { InputGroup } from '../../components/InputGroup/InputGroup';
import { Modal } from '../../components/Modal/Modal';
import { WITH_CREDENTIALS } from '../../components/utils';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ApiCreatedList } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import './CreateSubscriptionModal.css';
import { InputAlertMessage } from '../Form/InputAlertMessage';
import { WizardStepLabel } from './WizardStepLabel';

interface CreateSubscriptionModalProps {
	/*
	 * The id the api answered with, or null if it answered without one. `stillGuided` says whether
	 * anybody is still waiting to be led on from here, and has to be asked again after any awaiting
	 * of its own: the list still has to be refreshed for a customer who has moved on, but the wizard
	 * must not open behind the modal they have moved on to.
	 */
	onCreate: (listId: string | null, stillGuided: () => boolean) => void;
	subscriptionsCount: number;
}

export const CreateSubscriptionModal = ({
	onCreate,
	subscriptionsCount,
}: CreateSubscriptionModalProps): JSX.Element => {
	const [open, setOpen] = useState(false);
	const [name, setName] = useState('');
	const [valid, setValid] = useState<boolean | null>(null);
	const { api } = useConfig();
	const [loading, setLoading] = useState(false);
	const alerts = useAlerts();
	/*
	 * Which opening of the modal we are on. A save can still be in flight when the customer dismisses
	 * the modal and opens it again, and when it lands it must not close - or empty - the one they are
	 * typing in now. Locking the modal while saving would do it too, but a request that hangs would
	 * then trap them in it.
	 */
	const session = useRef(0);
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic, form: useTranslation().form };

	const validate = () => {
		setValid(name !== '');
	};

	const createSubscription = async (name: string): Promise<void> => {
		const startedIn = session.current;

		setLoading(true);

		const payload = { name };
		let listId: string | null = null;

		try {
			const { data } = await axios.post<ApiCreatedList>(`${api}me/lists.json`, payload, WITH_CREDENTIALS);

			// an answer without an id leads the customer nowhere, rather than to the wrong subscription
			listId = data?.listId !== undefined ? String(data.listId) : null;
		} catch (error) {
			// the modal used to close and report success here, whatever the server had said
			alerts.set({ type: 'error', message: t.couldNotSave });
			setLoading(false);
			return;
		}

		setLoading(false);

		/*
		 * Pressing the button is what asks to be led on, so a successful close does not end the
		 * session - opening the modal again, or walking away from it, is what does.
		 */
		const stillTheirs = () => session.current === startedIn;

		onCreate(listId, stillTheirs);

		if (stillTheirs()) closeModal();
	};

	const closeModal = () => {
		setOpen(false);
		setValid(null);
		alerts.clear();
		// the name went with the subscription that was just created; leaving it in the field would
		// now arm the button for a second one by the same name, a single click away
		setName('');
	};

	// walking away ends this opening of the modal, so a save still in flight leads nowhere
	const dismissModal = () => {
		session.current += 1;
		closeModal();
	};

	return (
		<>
			<Button
				style={{ marginBottom: '1.6rem' }}
				onClick={() => {
					session.current += 1;
					setOpen(true);
				}}
			>
				{subscriptionsCount > 0 ? t.createMoreSubscriptions : t.createSubscription}
			</Button>

			<Modal
				className="create-subscription-modal"
				show={open}
				disableBodyScroll
				onDismiss={() => dismissModal()}
				toggle={() => (open ? dismissModal() : setOpen(true))}
			>
				<ContentCard className="create-subscription-content">
					<div className="modal-header">
						<div className="modal-header-title">
							{/* the name is the first of the four, and the modal leads straight on to the rest */}
							<WizardStepLabel step={1} />
							{t.nameSubscription}
						</div>
						<button className="close-btn" onClick={() => dismissModal()}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className="modal-body">
						<InputGroup>
							<TextInput
								onBlur={() => {
									validate();
								}}
								placeholder={t.name}
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									// the "name missing" message is set on blur and used to stay put
									// while a name was being typed, so the same value was shown as
									// both valid and invalid
									if (e.target.value !== '') setValid(null);
								}}
							/>
							{/*
							 * The name is enough to go on. `valid` is only set on blur, so a customer who
							 * typed a name and reached straight for the button found it greyed out with
							 * nothing to tell them why - TitleEditInput next door has always read the name
							 * itself. `valid` stays for the message, which does belong after blur.
							 */}
							<Button disabled={loading || name === ''} onClick={() => createSubscription(name)}>
								{loading ? t.generic.saving : t.wizard.next}
							</Button>
						</InputGroup>
						{/* an example of what one of these is usually called, while there is nothing to go on */}
						{name === '' && <p className="name-example">{t.nameExample}</p>}
						{valid === false && (
							<InputAlertMessage type={'error'} message={`${t.form.name} ${t.missing}`} />
						)}
						<AlertMessages alerts={alerts} />
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
